import React, { useState, useEffect, useRef, useCallback } from 'react';
import configDetails from '../Config/Config';
import logger        from '../utils/Logger';
import { useReplayEngine }        from '../hooks/useReplayEngine';
import { transformReplayResponse } from '../utils/replayDataTransform';
import { BsPersonFill } from 'react-icons/bs';
import '../Styles/Replay.css';

const { baseUrl } = configDetails;

// ── format seconds → "M:SS"
function fmtS(s) {
  const total = Math.floor(s ?? 0);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

// ── format milliseconds → "M:SS"
function fmt(ms) {
  return fmtS((ms ?? 0) / 1000);
}

// ── group visible words by segment, preserving segment order
function groupBySegment(visibleWords, segments) {
  const map = new Map();
  visibleWords.forEach(w => {
    if (!map.has(w.segId)) map.set(w.segId, []);
    map.get(w.segId).push(w);
  });
  return segments
    .filter(s => map.has(s.id))
    .map(s => ({ segment: s, words: map.get(s.id) }));
}

// ── read user identity from localStorage
function getAuthenticatedUserDetails() {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// ── render a single non-speech item (image or rich text)
function NonSpeechItem({ item }) {
  if (item.type === 'image') {
    return (
      <div className="replay-nonspeech replay-nonspeech--image">
        <img
          src={item.content}
          alt="Shared during conversation"
          style={{ display: 'block', maxWidth: 300, width: '100%', height: 'auto', borderRadius: 8, margin: '4px 0' }}
          onError={e => {
            e.target.style.display = 'none';
            e.target.nextSibling && (e.target.nextSibling.style.display = 'block');
          }}
        />
        <div className="replay-nonspeech--image-error" style={{ display: 'none' }}>
          ⚠️ Image could not be loaded
        </div>
      </div>
    );
  }

  // rich_text — content is an HTML string
  return (
    <div
      className="replay-nonspeech replay-nonspeech--richtext"
      dangerouslySetInnerHTML={{ __html: item.content }}
    />
  );
}
const ReplayTab = ({
  conversationId,
  authIdToken,
  embedded        = false,
  currentTime     = 0, 
  isPlaying: externalIsPlaying = false,
  onSeek,
}) => {
  // ── Fetch state
  // 'idle' | 'loading' | 'processing' | 'unavailable' | 'error' | 'ready'
  const [fetchStatus, setFetchStatus] = useState('idle');
  const [errorMsg,    setErrorMsg]    = useState('');

  // Transformed replay data
  const [words,      setWords]      = useState([]);
  const [segments,   setSegments]   = useState([]);
  const [durationMs, setDurationMs] = useState(0);
  const [audioUrl,   setAudioUrl]   = useState(null);
  const [replayMode, setReplayMode] = useState(null); // 'word_level' | 'sentence_level'
  const [audioStartOffsetMs, setAudioStartOffsetMs] = useState(0);
  const [nonSpeechItems, setNonSpeechItems] = useState([]);

  // ── Fetch replay data
  useEffect(() => {
    if (!conversationId) return;

    const user   = getAuthenticatedUserDetails();
    const userId = user?.userSub;
    if (!userId) return;

    setFetchStatus('loading');
    setWords([]);
    setSegments([]);
    setDurationMs(0);
    setAudioUrl(null);
    setReplayMode(null);
    setNonSpeechItems([]);
    setAudioStartOffsetMs(0);

    logger.info('ReplayTab: fetching replay data', {
      component: 'ReplayTab',
      action:    'FETCH_REPLAY_START',
      userId,
      context:   { conversationId },
    });

    fetch(`${baseUrl}/replay/${conversationId}?user_id=${userId}`, {
      method:  'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization:  authIdToken,
      },
    })
      .then(async (res) => {
        if (res.status === 410) { setFetchStatus('unavailable'); return null; }
        if (res.status === 404) { setFetchStatus('error'); setErrorMsg('Conversation not found.');       return null; }
        if (res.status === 403) { setFetchStatus('error'); setErrorMsg('Access denied.');                return null; }
        if (!res.ok)            { setFetchStatus('error'); setErrorMsg(`Server error (${res.status}).`); return null; }
        return res.json();
      })
      .then((data) => {
        if (!data) return;

        if (data.status === 'processing') {
          setFetchStatus('processing');
          return;
        }

        try {
          const { words: w, segments: s, durationMs: d, mode, non_speech: ns, audioStartOffsetMs: audioOffset } =
            transformReplayResponse(data);

          setWords(w);
          setSegments(s);
          setDurationMs(d);
          setAudioUrl(data.audio_url ?? null);
          setReplayMode(mode);
          setNonSpeechItems(ns ?? []);
          setAudioStartOffsetMs(audioOffset ?? 0);
          setFetchStatus('ready');

          logger.info('ReplayTab: replay data ready', {
            component: 'ReplayTab',
            action:    'FETCH_REPLAY_SUCCESS',
            userId,
            context:   { conversationId, mode, wordCount: w.length, nonSpeechCount: (ns ?? []).length },
          });
        } catch (transformErr) {
          logger.warn('ReplayTab: transform failed', {
            component: 'ReplayTab',
            action:    'TRANSFORM_ERROR',
            error:     transformErr,
          });
          setFetchStatus('unavailable');
        }
      })
      .catch((err) => {
        logger.error('ReplayTab: fetch failed', {
          component: 'ReplayTab',
          action:    'FETCH_REPLAY_ERROR',
          context:   { conversationId },
          error:     err,
        });
        setFetchStatus('error');
        setErrorMsg('Failed to load replay data.');
      });
  }, [conversationId, authIdToken]);

  // ── Replay engine (rAF + audio clock) — standalone mode only ────────────
  const engine = useReplayEngine(words, durationMs);

  // ── In embedded mode derive display state from the parent audio player ───
  const currentMs   = embedded ? currentTime * 1000 : engine.currentMs;
  const isPlaying   = embedded ? externalIsPlaying   : engine.isPlaying;
  const isBuffering = embedded ? false                : engine.isBuffering;
  const isFinished  = embedded ? false                : engine.isFinished;
  const { togglePlay, seekTo, reset, audioRef, doPlay } = engine;
  const absoluteMs      = currentMs + audioStartOffsetMs;
  const visibleWords    = words.filter(w => w.start_s * 1000 <= absoluteMs);
  const visibleNonSpeech = nonSpeechItems.filter(ns => (ns.offset_ms ?? 0) <= absoluteMs);
  const activeWord      = words.find(
    w => w.start_s * 1000 <= absoluteMs && w.end_s * 1000 > absoluteMs
  );
  const activeWordId = activeWord?.id ?? null;

  // ── Override durationMs from actual audio metadata ────────────────────────
  const handleLoadedMetadata = useCallback((e) => {
    const actualMs = Math.round(e.target.duration * 1000);
    if (actualMs > 0) setDurationMs(actualMs);
  }, []);

  // ── Auto-scroll active word into view (centers latest word in viewport)
  const activeRowRef = useRef(null);
  const activeWordRef = useRef(null);
  useEffect(() => {
    const target = activeWordRef.current ?? activeRowRef.current;
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [activeWordId]);

  // ── Click a word: seek the scrubber + audio to that word, then start playing
  const handleWordClick = useCallback((word) => {
    if (embedded) {
      onSeek?.(word.start_s);
    } else {
      seekTo(word.start_s * 1000 - audioStartOffsetMs);
      setTimeout(() => doPlay?.(), 50);
    }
  }, [embedded, onSeek, seekTo, doPlay, audioStartOffsetMs]);
  // ── Grouped rows 
  const groupedRows = groupBySegment(visibleWords, segments);

  if (fetchStatus === 'idle') {
    return embedded ? (
      <div className="replay-state-screen replay-state-screen--sm">
        <p className="text-muted mb-0 small">Initialising replay…</p>
      </div>
    ) : null;
  }

  if (fetchStatus === 'loading') {
    return (
      <div className="replay-state-screen replay-state-screen--loading">
        <div className="replay-loading-wave" aria-hidden="true">
          {[0,1,2,3,4,5,6,7,8,9,10,11,12,13,14].map(i => (
            <span key={i} className="replay-loading-bar"
              style={{ animationDelay: `${(i * 0.09).toFixed(2)}s` }} />
          ))}
        </div>
        <p className="replay-loading-label">Loading replay…</p>
        <p className="replay-loading-sub">Fetching word timings</p>
      </div>
    );
  }

  if (fetchStatus === 'processing') {
    return (
      <div className="replay-state-screen">
        <div className="replay-state-icon">⏳</div>
        <h6 className="fw-semibold text-dark mb-1">Audio is still processing</h6>
        <p className="text-muted mb-0 replay-state-desc">
          Replay will be available once the audio has finished processing. Check back in a moment.
        </p>
      </div>
    );
  }

  if (fetchStatus === 'unavailable') {
    return (
      <div className="replay-state-screen">
        <div className="replay-state-icon">🎙️</div>
        <h6 className="fw-semibold text-dark mb-1">Replay not available</h6>
        <p className="text-muted mb-0 replay-state-desc">
          Word-level replay is only available for recordings made with Deepgram.
          This note was recorded with an earlier engine.
        </p>
      </div>
    );
  }

  if (fetchStatus === 'error') {
    return (
      <div className="replay-state-screen">
        <div className="replay-state-icon">⚠️</div>
        <h6 className="fw-semibold text-dark mb-1">Could not load replay</h6>
        <p className="text-muted mb-0">{errorMsg}</p>
      </div>
    );
  }

  if (fetchStatus !== 'ready') return null;

  return (
    <div className={`replay-container${embedded ? ' replay-embedded' : ''}`}>

      {/* Hidden audio element — standalone mode only */}
      {!embedded && audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          preload="auto"
          onLoadedMetadata={handleLoadedMetadata}
          style={{ display: 'none' }}
        />
      )}

      {/* ── Controls bar — standalone only ── */}
      {!embedded && (
        <div className="replay-controls">
          <button
            className="replay-play-btn"
            onClick={isFinished ? reset : togglePlay}
            aria-label={isFinished ? 'Restart' : isPlaying ? 'Pause' : 'Play'}
            title={isFinished ? 'Restart' : isPlaying ? 'Pause' : 'Play'}
            disabled={fetchStatus !== 'ready'}
          >
            {isBuffering ? '⏳' : isFinished ? '↺' : isPlaying ? '⏸' : '▶'}
          </button>
          <span className="replay-time">{fmt(currentMs)}</span>
          <input
            type="range"
            min={0}
            max={durationMs}
            value={currentMs}
            step={50}
            className="replay-scrubber"
            aria-label="Seek"
            onChange={e => seekTo(Number(e.target.value))}
          />
          <span className="replay-time" style={{ textAlign: 'right' }}>{fmt(durationMs)}</span>
          <span className={`replay-badge ${isPlaying ? 'playing' : isFinished ? 'finished' : 'paused'}`}>
            {isBuffering ? 'buffering' : isPlaying ? 'playing' : isFinished ? 'finished' : 'paused'}
          </span>
        </div>
      )}

      {/* ── Transcript replay area ── */}
      <div
        className="replay-transcript"
        role="log"
        aria-live="polite"
        aria-atomic="false"
      >
        {groupedRows.length === 0 && visibleNonSpeech.length === 0 ? (
          /* ── Waiting / idle state ── */
          <div className="replay-waiting">
            {(isPlaying || currentMs > 0) ? (
              <>
                <div className="replay-waiting__wave" aria-hidden="true">
                  {[0,1,2,3,4,5,6,7,8,9,10,11,12,13,14].map(i => (
                    <span key={i} className="replay-waiting__bar"
                      style={{ animationDelay: `${(i * 0.09).toFixed(2)}s` }} />
                  ))}
                </div>
                <div className="replay-waiting__label">
                  <span className="replay-waiting__dot" aria-hidden="true" />
                  Listening for speech…
                </div>
                <p className="replay-waiting__sub">
                  Words will appear here as the audio plays
                </p>
              </>
            ) : (
              /* Idle — not started yet */
              <>
                <div className="replay-waiting__idle-icon" aria-hidden="true">
                  {[0,1,2,3,4].map(i => (
                    <span key={i} className="replay-waiting__idle-bar"
                      style={{ height: `${[14,22,30,22,14][i]}px` }} />
                  ))}
                </div>
                <p className="replay-waiting__label replay-waiting__label--idle">
                  {embedded ? 'Press ▶ to begin replay' : 'Press ▶ to begin replay'}
                </p>
                <p className="replay-waiting__sub">
                  Words sync with the audio as it plays
                </p>
              </>
            )}
          </div>
        ) : (() => {
          const timeline = [];

          groupedRows.forEach(({ segment, words: segWords }) => {
            timeline.push({ kind: 'speech', segment, words: segWords, timeMs: segment.start_s * 1000 });
          });

          visibleNonSpeech.forEach(ns => {
            timeline.push({ kind: 'nonspeech', item: ns, timeMs: ns.offset_ms });
          });

          // Sort by timestamp so inserted text / images appear in the correct position
          timeline.sort((a, b) => a.timeMs - b.timeMs);

          return timeline.map((entry, idx) => {
            if (entry.kind === 'nonspeech') {
              const ns = entry.item;
              const key = ns.div_id ?? `ns_${idx}`;
              return (
                <div key={key} className="replay-segment-row replay-segment-row--nonspeech">
                  <div className="replay-seg-body">
                    {/* Person icon + timestamp (same style as LiveTranscript) */}
                    <div className="transcript-speaker-header">
                      <div className="transcript-speaker-avatar">
                        <BsPersonFill />
                      </div>
                      <span className="transcript-paragraph-timestamp">{fmt(ns.offset_ms)}</span>
                    </div>
                    <div style={{ paddingLeft: '50px' }}>
                      <NonSpeechItem item={ns} />
                    </div>
                  </div>
                </div>
              );
            }

            // speech entry
            const { segment, words: segWords } = entry;
            const hasActive = segWords.some(w => w.id === activeWordId);
            return (
              <div
                key={segment.id}
                ref={hasActive ? activeRowRef : null}
                className={`replay-segment-row${hasActive ? ' replay-segment-row--active' : ''}`}
              >
                <div className="replay-seg-body">
                  {/* Person icon + timestamp (same style as LiveTranscript) */}
                  <div className="transcript-speaker-header">
                    <div className="transcript-speaker-avatar">
                      <BsPersonFill />
                    </div>
                    <span className="transcript-paragraph-timestamp">{fmtS(segment.start_s)}</span>
                  </div>
                  <div className="replay-seg-text">
                    {segWords.map(w => {
                      const isActive = w.id === activeWordId;
                      return (
                        <span
                          key={w.id}
                          ref={isActive ? activeWordRef : null}
                          className={`replay-word ${isActive ? 'active' : 'done'}`}
                          onClick={() => handleWordClick(w)}
                          title={`Seek to ${fmtS(w.start_s)}`}
                          style={{ cursor: 'pointer' }}
                        >
                          {w.word}{' '}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          });
        })()}
      </div>

      {/* ── Skip buttons — standalone only ── */}
      {!embedded && (
        <div className="replay-skip-row">
          <button className="replay-skip-btn" onClick={reset}>↺ Reset</button>
          {[
            { label: '0:10', val: 10_000 },
            { label: '0:30', val: 30_000 },
            { label: '1:00', val: 60_000 },
          ]
            .filter(({ val }) => val < durationMs)
            .map(({ label, val }) => (
              <button key={label} className="replay-skip-btn" onClick={() => seekTo(val)}>
                Skip to {label}
              </button>
            ))}
        </div>
      )}

      {/* Sentence-level fallback notice */}
      {!embedded && replayMode === 'sentence_level' && (
        <p className="replay-fallback-note">
          Word-level timing unavailable — showing sentence-level replay.
        </p>
      )}
    </div>
  );
};

export default ReplayTab;